/**
 * Panel de faltantes: materiales bloqueantes que quedan en negativo con el
 * plan actual. Es la lista de "necesidades de compra": qué falta, cuánto,
 * desde qué semana y cuándo llega la próxima entrada. Exportable a CSV para
 * el departamento de compras.
 */
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { fmtMiles } from '@/utils/planProduccion/engine'
import { useFmtArticulo } from '@/hooks/useFmtArticulo'

export interface ShortageRow {
  leafKey: string
  descripcion: string
  codigo: string
  firstWeekLabel: string
  maxDeficit: number
  nextInflowLabel: string | null
}

interface Props {
  dark: boolean
  items: ShortageRow[]
  onClose: () => void
}

function downloadCsv(items: ShortageRow[], headers: string[], fmtArt: (code: string | null | undefined) => string) {
  const esc = (s: string) => `"${s.replace(/"/g, '""')}"`
  const lines = [
    headers.map(esc).join(';'),
    ...items.map((i) =>
      [
        esc(i.descripcion),
        esc(fmtArt(i.codigo)),
        esc(i.firstWeekLabel),
        String(Math.ceil(i.maxDeficit)),
        esc(i.nextInflowLabel ?? '—'),
      ].join(';'),
    ),
  ]
  const blob = new Blob([`﻿${lines.join('\r\n')}`], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = 'necesidades_compra.csv'
  link.click()
  URL.revokeObjectURL(url)
}

export function ShortagesPanel({ dark, items, onClose }: Props) {
  const { t } = useTranslation()
  const fmtArt = useFmtArticulo()

  const cardCls = dark
    ? 'rounded-xl border border-red-500/25 bg-slate-900'
    : 'rounded-xl border border-red-300 bg-white shadow-sm'
  const rowCls = dark
    ? 'border-white/8 hover:bg-white/[0.04]'
    : 'border-slate-100 hover:bg-red-50/40'
  const headCls = clsx('text-[9px] font-bold uppercase tracking-wider', dark ? 'text-slate-500' : 'text-slate-400')

  return (
    <div className={cardCls}>
      <div className={clsx('flex items-center gap-2 px-4 py-2.5 border-b', dark ? 'border-white/8' : 'border-slate-100')}>
        <span className="text-[13px]">🛒</span>
        <div className="flex-1 min-w-0">
          <div className={clsx('text-[12px] font-bold', dark ? 'text-slate-100' : 'text-slate-800')}>
            {t('planFormato.faltantesTitulo')}
          </div>
          <div className={clsx('text-[10px]', dark ? 'text-slate-500' : 'text-slate-400')}>
            {t('planFormato.faltantesSub', { count: items.length })}
          </div>
        </div>
        {items.length > 0 && (
          <button
            onClick={() =>
              downloadCsv(items, [
                t('planFormato.faltantesColMaterial'),
                t('planFormato.faltantesColCodigo'),
                t('planFormato.faltantesColSemana'),
                t('planFormato.faltantesColDeficit'),
                t('planFormato.faltantesColEntrada'),
              ], fmtArt)
            }
            className={clsx(
              'shrink-0 text-[10px] font-bold px-2.5 py-1 rounded-lg border transition-colors cursor-pointer',
              dark
                ? 'border-white/15 bg-white/5 text-slate-300 hover:bg-white/10'
                : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50',
            )}
          >
            ⬇ CSV
          </button>
        )}
        <button
          onClick={onClose}
          className={clsx('shrink-0 text-[11px] px-2 py-1 rounded-lg transition-colors', dark ? 'text-slate-400 hover:bg-white/10' : 'text-slate-500 hover:bg-slate-100')}
        >
          ✕
        </button>
      </div>

      {items.length === 0 ? (
        <div className={clsx('px-4 py-6 text-center text-[11px]', dark ? 'text-slate-500' : 'text-slate-400')}>
          {t('planFormato.faltantesVacio')}
        </div>
      ) : (
        <div className="max-h-[240px] overflow-auto">
          <div className={clsx('flex items-center gap-3 px-4 py-1.5 border-b sticky top-0', dark ? 'border-white/8 bg-slate-900' : 'border-slate-100 bg-white')}>
            <span className={clsx(headCls, 'flex-1')}>{t('planFormato.faltantesColMaterial')}</span>
            <span className={clsx(headCls, 'w-14 text-center shrink-0')}>{t('planFormato.faltantesColSemana')}</span>
            <span className={clsx(headCls, 'w-20 text-right shrink-0')}>{t('planFormato.faltantesColDeficit')}</span>
            <span className={clsx(headCls, 'w-16 text-center shrink-0')}>{t('planFormato.faltantesColEntrada')}</span>
          </div>
          {items.map((i) => (
            <div
              key={i.leafKey}
              className={clsx('flex items-center gap-3 px-4 py-1.5 border-b last:border-b-0 transition-colors', rowCls)}
            >
              <div className="min-w-0 flex-1 leading-tight">
                <div className={clsx('text-[11px] font-semibold truncate', dark ? 'text-slate-100' : 'text-slate-800')}>
                  {i.descripcion}
                </div>
                <div className={clsx('text-[9px] truncate', dark ? 'text-slate-500' : 'text-slate-400')}>
                  {fmtArt(i.codigo)}
                </div>
              </div>
              <span className={clsx('shrink-0 w-14 text-center text-[10px] font-bold rounded px-1 py-0.5', dark ? 'bg-red-500/20 text-red-300' : 'bg-red-100 text-red-700')}>
                {i.firstWeekLabel}
              </span>
              <span className={clsx('shrink-0 w-20 text-right text-[11px] font-black tabular-nums', 'text-red-500')}>
                −{fmtMiles(i.maxDeficit)}
              </span>
              <span className={clsx('shrink-0 w-16 text-center text-[10px] font-semibold tabular-nums', i.nextInflowLabel ? (dark ? 'text-sky-300' : 'text-sky-600') : dark ? 'text-slate-600' : 'text-slate-400')}>
                {i.nextInflowLabel ?? '—'}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
